/* eslint-disable react-hooks/exhaustive-deps */
import { makeStyles } from '@mui/styles';
import Slider from 'apollo-react/components/Slider';
import TextField from 'apollo-react/components/TextField/TextField';
import React, { useEffect, useRef, useState } from 'react';

import {
  getEvaluatedConditions,
  getEvaluatedExpression,
} from './../selectors/form-manager.selectors';
import { useFormSelector, useSsNumericTextBoxParams } from '../../custom-hooks';
import { getUITA } from '../../utils/commonUtils';
import Browser from '../../utils/pdf-print-js/browser';
import { isNilOrEmpty } from '../../utils/ramdaUtils';
import { FORM_MODEL_ATTRIBUTES } from '../constants/form-manager.constants';
import { getDisplayValue, safeToFixedDecimalScale } from '../utils/controls/ssNumericTextBox.utils';
import { showAsteriskBefore } from '../utils/form-manager.utils';

const useStyles = makeStyles({
  sliderWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  sliderTrack: {
    width: '100%',
    paddingRight: 30,
    maxWidth: ({ isEditor }) => (isEditor ? 440 : null),
  },
  sliderInput: {
    maxWidth: 75,
    padding: ({ isEditor }) => (isEditor ? 4 : 0),
    paddingTop: ({ isEditor }) => (isEditor ? 10 : 4),
  },
});

const SSSlider = ({
  id,
  index,
  label,
  actions,
  isRequired,
  value = 0,
  error,
  entityName,
  fieldName,
  inTable,
  inFieldGroup,
  isHiddenLabel,
  instanceId,
  isEditor,
  disabled,
}) => {
  const classes = useStyles({ isEditor });

  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [hasValueChanged, setHasValueChanged] = useState(false);

  const selectorProps = { id, index, entityName };
  const maxSliderValue = useFormSelector(getEvaluatedExpression, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.MAX_VALUE,
    defaultValue: 100,
  });
  const minSliderValue = useFormSelector(getEvaluatedExpression, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.MIN_VALUE,
    defaultValue: 0,
  });
  const sliderShowInput = useFormSelector(getEvaluatedConditions, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.SHOW_INPUT,
    defaultValue: true,
  });
  const ref = useRef(null);

  const { dataType, decimalScale } = useSsNumericTextBoxParams({
    entityName,
    fieldName,
    value: localValue,
  });

  const displayValue = getDisplayValue({ isFocused, hasValueChanged, decimalScale, localValue });
  const hiddenLabel = inTable || (inFieldGroup && isHiddenLabel);

  // Update inner state when `value` is changed from outside.
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleBlur = ({ target: { value: blurValue } }, isSliderBlur) => {
    const controlValue = isSliderBlur ? displayValue?.toString() : blurValue?.toString();

    if ((isNilOrEmpty(value) && controlValue === '') || controlValue === value?.toString()) {
      return;
    }

    if (controlValue === '') {
      actions.updateControlValue(0);
      return;
    }

    let newLocalValue = controlValue;
    const valueToStore =
      controlValue?.includes('e') && !Browser.isFirefox()
        ? +controlValue.split('e')[0]
        : +controlValue;

    actions.updateControlValue(valueToStore);

    switch (dataType) {
      case 'System.Int32': {
        newLocalValue = parseInt(controlValue);
        break;
      }
      case 'System.Decimal': {
        newLocalValue = +safeToFixedDecimalScale(decimalScale, controlValue);
        break;
      }
      default:
        // eslint-disable-next-line no-console
        console.log(`Unhandled dataType: ${dataType}`);
    }

    setLocalValue(newLocalValue);
    setHasValueChanged(true);
    setIsFocused(false);
  };

  const handleChange = ({ target }, isInputChange) => {
    // we need to manually set focus to the field, when value is changed by stepper arrows
    // some browsers like Firefox does not set focus automatically

    if (isInputChange) {
      if (process.env.NODE_ENV === 'test') {
        setIsFocused(true); // forcing focused state on test environment when we interact with the field
      } else {
        ref.current.focus(); // by some reason it causes tests to fail
      }
    }

    setHasValueChanged(false);
    setLocalValue(target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key.toLowerCase() === 'e') {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    const pastedContent = e.clipboardData.getData('text');

    if (pastedContent.toLowerCase().includes('e')) {
      e.preventDefault();
    }
  };

  return (
    <div
      ui-ta={getUITA('FormCtrl', 'SSSlider', entityName, fieldName)}
      className={classes.sliderWrapper}
    >
      <div className={classes.sliderTrack}>
        <Slider
          label={hiddenLabel ? '' : label}
          asteriskPosition={showAsteriskBefore(inTable ? '' : label, inTable) ? 'before' : 'after'}
          required={isRequired}
          value={Number(displayValue) ?? 0}
          error={error}
          disabled={isEditor || disabled}
          min={Number(minSliderValue)}
          max={Number(maxSliderValue)}
          onChange={(event) => {
            handleChange(event, false);
          }}
          onBlur={(e) => handleBlur(e, true)}
          style={{ marginLeft: isEditor ? 10 : 5 }}
        />
      </div>
      {sliderShowInput && (
        <TextField
          id={instanceId}
          type={Browser.isFirefox() ? 'text' : 'number'}
          className={classes.sliderInput}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onChange={(event) => {
            handleChange(event, true);
          }}
          value={displayValue ?? 0}
          error={error}
          disabled={isEditor || disabled}
          onWheel={() => {
            const input = ref.current;
            if (input && document.activeElement === input) {
              // this will prevent accidental scroll to change inputs value
              input.blur();
            }
          }}
          inputProps={{
            ref,
            style: { fontSize: 14 },
          }}
        />
      )}
    </div>
  );
};

export default SSSlider;
